import debugConstructor from 'debug'
import each from 'lodash/each'
import find from 'lodash/find'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import isUndefined from 'lodash/isUndefined'
import keyBy from 'lodash/keyBy'
import uniqueId from 'lodash/uniqueId'

import * as builderActions from 'common/builder/actions/builderActions'
import * as contentActions from 'common/builder/actions/contentActions'
import { deleteNode } from 'common/builder/actions/deleteNode'
import * as ActionTypes from 'common/builder/constants/builderReduxActionTypes'
import { NodeType } from 'common/builder/constants/NodeType'
import batchAdapter from 'common/builder/models/Batch/adapter'
import {
  isSameContent,
  normalizeContentData,
  getMissingExternalTargetIds,
} from 'common/builder/models/Batch/utils'
import nodeRegistry from 'common/builder/nodeRegistry'
import builderSelectors from 'common/builder/selectors/builder'
import * as commonSelectors from 'common/builder/selectors/builder/commonSelectors'
import * as entitySelectors from 'common/builder/selectors/builder/entitySelectors'
import * as managerSelectors from 'common/builder/selectors/managerSelectors'
import { getConnectedChannels } from 'common/builder/selectors/managerSelectors'
import { getBotTimezone } from 'common/core/selectors/appSelectors'
import { fetchFullFlow } from 'common/flow/actions/flowActions'
import * as flowSelectors from 'common/flow/selectors/flowSelectors'
import { NotificationReasonActions } from 'common/oneTimeNotify/store'
import { getQuery, getPathname, navigatePollyfill } from 'utils/router/tools'
import { analyticsService } from 'utils/services/analytics'
import errorTrackingService from 'utils/services/errorTrackingService'

import { getContent } from '../builderAsyncActions'

import * as autoSaveActions from './autoSaveActions'

const debug = debugConstructor('manager')

/**
 * @param managerId
 */
export function fetchFlowAndCreateBuilder(managerId, options = {}) {
  return async (dispatch, getState) => {
    const manager = managerSelectors.getById(getState(), managerId)
    if (!manager || !manager.flowId) {
      return
    }

    const flow = await dispatch(fetchFullFlow(manager.flowId, { chId: options.chId ?? null }))
    const managerExists = Boolean(managerSelectors.getById(getState(), managerId))
    if (!managerExists || !flow) {
      return
    }
    dispatch(createBuilder(manager.id, flow))
  }
}

/**
 * @param managerId
 * @param flow
 * @todo re-implement with TDD
 */
export function createBuilder(managerId, flow) {
  return async (dispatch, getState, context) => {
    const state = getState()

    const manager = managerSelectors.getById(state, managerId)
    const builderOptions = {
      ...manager.builderOptions,
      sidebarHidden: !manager.viewerMode,
      flowChartMode: commonSelectors.isFlowChartModeSaved(manager.flowId, true),
      builderId: uniqueId(`${manager.flowId}__`),
      flowId: manager.flowId,
    }
    const query = getQuery()
    const pathname = getPathname()
    const updatedQuery = { ...query }

    const initialNodesPreset = query?.preset
    if (initialNodesPreset) {
      delete updatedQuery.preset

      builderOptions.preventOpenInitializedRootNode = true
    }

    const triggerPickerSection = query?.triggerPickerSection
    if (triggerPickerSection) {
      delete updatedQuery.triggerPickerSection

      builderOptions.triggerPickerSectionToOpenAfterBuilderIsReady = Number(triggerPickerSection)
    }

    const openTriggerId = query?.openTriggerId
    if (openTriggerId) {
      delete updatedQuery.openTriggerId

      builderOptions.openTriggerId = openTriggerId
    }

    const queryWasChanged = !isEqual(query, updatedQuery)
    if (queryWasChanged) {
      const searchParamsString = new URLSearchParams(updatedQuery).toString()
      const url = searchParamsString ? `${pathname}?${searchParamsString}` : pathname
      navigatePollyfill(url, { replace: true })
    }

    let batch = null
    const defaultCoordinates = {}
    if (manager.viewerMode) {
      // forming initialData for the viewer
      const coordinates = isEmpty(flow.coordinates) ? {} : flow.coordinates
      builderOptions.coordinates = Object.assign({ ...defaultCoordinates }, coordinates)

      batch = {
        contents: [...flow.contents],
        root_content: flow.contentId,
      }
    } else {
      // forming initialData for the builder
      builderOptions.coordinates = Object.assign(
        { ...defaultCoordinates },
        flow.coordinates,
        flow.draftCoordinates,
      )

      const draftContents = flow.draftContents
      const publishedContents = flow.contents
      const publishedWithoutChanges = publishedContents.filter((content) => {
        return !find(draftContents, (draftContent) => isSameContent(draftContent, content))
      })

      // PATCH restoring content_id for draft contents
      // content_id is lost if the data received from the server is not updated after publication
      const publishedContentIdMap = keyBy(
        publishedContents,
        (content) => normalizeContentData(content)._oid,
      )
      const fixedContentIds = []
      draftContents.forEach((content) => {
        const draftContent = normalizeContentData(content)
        if (!draftContent.content_id && publishedContentIdMap[draftContent._oid]) {
          content.content_id = publishedContentIdMap[draftContent._oid].content_id
          fixedContentIds.push(content.content_id)
        }
      })
      if (fixedContentIds.length) {
        const msg = `content_id restore: some contents were fixed`
        debug(msg, fixedContentIds)
        errorTrackingService.trackWarning(msg, {
          extra: { fixedContentIds },
          fingerprint: 'content-id-restore',
        })
      }

      batch = {
        contents: [...draftContents, ...publishedWithoutChanges],
        root_content: !isUndefined(flow.draftContentId) ? flow.draftContentId : flow.contentId,
      }
    }

    // to render the flow chart, external contents must be loaded
    // the backend gives us all external contents for the published version of the flow
    // but this is not enough - the draft may contain links to other external contents
    // we find them and download
    const missingIds = getMissingExternalTargetIds(batch, builderOptions.flowId)
    if (missingIds.length) {
      errorTrackingService.trackWarningOnce('Missing contents in builder', {
        extra: { missingIds },
        fingerprint: 'missing-contents-in-builder',
      })
    }
    debug('load missing contents', missingIds)
    const missingContents = await dispatch(getContents(managerId, missingIds))

    const doesManagerExist = Boolean(managerSelectors.getById(getState(), managerId))
    if (!doesManagerExist) {
      return
    }

    batch = {
      ...batch,
      contents: [...batch.contents, ...missingContents],
    }
    // OTN reasons list needed for render NTN buttons
    await dispatch(NotificationReasonActions.getTopicsListFx({ active_only: false }))

    const botTimeZone = getBotTimezone(getState())
    let parsed = batchAdapter.parseBatch(batch, { botTimeZone })

    const startingStepNode = nodeRegistry
      .getByType(NodeType.STARTING_STEP)
      .create({ id: NodeType.STARTING_STEP })

    builderOptions.initialData = {
      ...parsed,
      nodes: [...parsed.nodes, startingStepNode],
    }
    builderOptions.rootId = parsed.rootId

    const isEmptyNotPublishedFlow = !flow.draftContents.length && !flow.contents.length

    builderOptions.createInitialRootNodeType = null
    if (isEmptyNotPublishedFlow) {
      const nodeTypeBasedOnFlowTriggers = flowSelectors.getFlowPrimaryNodeType(state, flow.id)
      const connectedChannels = getConnectedChannels(state)

      if (builderOptions.initialNodeType) {
        builderOptions.createInitialRootNodeType = builderOptions.initialNodeType
      } else if (initialNodesPreset) {
        builderOptions.createInitialRootNodeType = null
      } else if (nodeTypeBasedOnFlowTriggers) {
        builderOptions.createInitialRootNodeType = nodeTypeBasedOnFlowTriggers
      } else if (connectedChannels.length === 1) {
        builderOptions.createInitialRootNodeType = connectedChannels[0].nodeType
      } else {
        builderOptions.createInitialRootNodeType = null
      }
    }

    if (builderOptions.createInitialRootNodeType !== null) {
      builderOptions.preventOpenInitializedRootNode = true
    }

    builderOptions.quick_campaign_data = flow.quick_campaign_data

    dispatch(builderActions.createBuilderState(builderOptions))
    dispatch(
      updateManagerState(manager.id, {
        builderId: builderOptions.builderId,
        // builder not ready before we apply initial nodes preset
        isBuilderReady: !initialNodesPreset,
      }),
    )

    if (!manager.viewerMode) {
      dispatch(autoSaveActions.enableAutoSave(manager.id))
    }
    if (initialNodesPreset) {
      await dispatch(
        builderActions.applyInitialNodesPreset(builderOptions.builderId, initialNodesPreset),
      )
      dispatch(updateManagerState(manager.id, { isBuilderReady: true }))
    }
  }
}

export function createIndependentBuilder(managerId) {
  return async (dispatch, getState, context) => {
    const state = getState()

    const manager = managerSelectors.getById(state, managerId)

    const doesManagerExist = Boolean(manager)
    if (!doesManagerExist) {
      return
    }

    const botTimeZone = getBotTimezone(getState())
    const batch = {
      contents: [],
      root_content: null,
    }
    const parsed = batchAdapter.parseBatch(batch, { botTimeZone })

    const builderOptions = {
      ...manager.builderOptions,
      sidebarHidden: true,
      flowChartMode: true,
      builderId: uniqueId(`${manager.flowId}__`),
      flowId: manager.flowId,
      coordinates: {},
      initialData: parsed,
      rootId: parsed.rootId,
      createInitialRootNodeType: null,
      disableFlowChartInteractions: true,
    }

    dispatch(builderActions.createBuilderState(builderOptions))
    dispatch(
      updateManagerState(manager.id, {
        builderId: builderOptions.builderId,
        isBuilderReady: true,
      }),
    )
  }
}

/**
 * @param managerId
 */
export function removeBuilder(managerId, options = { tryAutoSave: true }) {
  return (dispatch, getState) => {
    const state = getState()
    const manager = managerSelectors.getById(state, managerId)
    const builderId = get(manager, 'builderId', null)

    if (!builderId) {
      return
    }

    dispatch(autoSaveActions.disableAutoSave(managerId))
    if (options.tryAutoSave && !manager.viewerMode) {
      dispatch(autoSaveActions.tryAutoSave(managerId, { force: true }))
    }
    dispatch(updateManagerState(managerId, { builderId: null }))
    dispatch(builderActions.removeBuilderState(builderId))
  }
}

/**
 * editor wrapper around {builderStateActions.applyBatch}

 * @param managerId
 * @param rawBatch
 * @param options
 *  keepChanged - do not reset the changed flag
 */
export function applyBuilderBatch(managerId, rawBatch, options = {}) {
  return (dispatch, getState) => {
    const state = getState()
    const manager = managerSelectors.getById(state, managerId)
    const builderId = get(manager, 'builderId', null)

    if (!builderId) {
      return
    }

    const rootId = builderSelectors.builderState.getRootNodeId(getState(), builderId)
    const nodeIdMap = builderSelectors.node.getNodeIdMap(getState(), builderId)
    const botTimeZone = getBotTimezone(getState())
    const parsed = batchAdapter.parseBatch(rawBatch, {
      nodeIdMap,
      botTimeZone,
      isPartialPatchParse: true,
    })
    const rootIdChanged = !isUndefined(parsed.rootId) && parsed.rootId !== rootId
    dispatch(builderActions.applyParsed(builderId, parsed, options))
    if (rootIdChanged) {
      dispatch(builderActions.applyRootId(builderId, parsed.rootId))
    }

    // download all external contents, they may be useful on the flow chart
    dispatch(contentActions.ensureForeignContents(builderId))

    // update lastContentHashMap and lastRootId to support the functionality of auto-saving
    const changedContentIds = parsed.nodes.map((content) => content.id)
    dispatch(autoSaveActions.commitLastContentHashMap(managerId, changedContentIds))
    if (rootIdChanged) {
      dispatch(autoSaveActions.commitLastRootId(managerId))
    }

    return parsed
  }
}

/**
 * editor wrapper around {builderStateActions.applyCoordinates}

 * @param managerId
 * @param coordinates
 */
export function applyBuilderCoordinates(managerId, coordinates) {
  return (dispatch, getState) => {
    const state = getState()
    const manager = managerSelectors.getById(state, managerId)
    const builderId = get(manager, 'builderId', null)

    if (!builderId) {
      return
    }
    dispatch(builderActions.applyCoordinates(builderId, coordinates))

    // if we receive null instead of coordinates, it means the content has been deleted
    // we will remove such contents from the builder
    const builder = builderSelectors.state.getById(getState(), builderId)
    each(coordinates, (coords, id) => {
      if (!coords) {
        const content = entitySelectors.getNodeById(getState(), builderId, id)
        if (!content) {
          return
        }
        const foreign = builder.flow !== content.flow
        if (!foreign) {
          return
        }
        return dispatch(deleteNode(builderId, content.id))
      }
    })

    // update lastCoordinates to support the functionality of auto-saving
    const changedNodeIds = Object.keys(coordinates)
    dispatch(autoSaveActions.commitLastCoordinates(managerId, changedNodeIds))
  }
}

/**
 * @param managerId
 * @param contentIds
 */
export function getContents(managerId, contentIds) {
  return (dispatch, getState, context) => {
    const manager = managerSelectors.getById(getState(), managerId)

    if (!manager || !contentIds) {
      return []
    }

    const { flowId, loadStats } = manager

    const promises = contentIds.map((contentId) => {
      return dispatch(getContent(contentId, flowId, loadStats))
    })

    return window.Promise.all(promises)
  }
}

/**
 * @param managerId
 * @param changes
 */
export function updateManagerState(managerId, changes) {
  return {
    type: ActionTypes.UPDATE_MANAGER_STATE,
    managerId,
    changes,
  }
}

/**
 * @param managerId
 */
export function removeManagerState(managerId) {
  return (dispatch, getState) => {
    return dispatch({
      type: ActionTypes.REMOVE_MANAGER_STATE,
      managerId,
    })
  }
}

export const setContentHolderId = (flowId, chId) => {
  return (dispatch, getState) => {
    const managers = managerSelectors.filter(getState(), { flowId })

    for (const manager of managers) {
      dispatch(removeBuilder(manager.id))
      dispatch(fetchFlowAndCreateBuilder(manager.id, { chId }))
    }

    analyticsService.sendEvent('FLOW_BUILDER.STATS_TRIGGER.CHANGED')
  }
}
